import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CalendarIcon, CalendarDaysIcon, ListIcon } from 'lucide-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { parseAsStringLiteral, useQueryState } from 'nuqs';

import {
  agendaRange,
  getCalendarMatrix,
  gridRange,
  startOfMonth,
} from '@/components/calendar/helpers';
import type { CalendarEvent, CalendarView } from '@/components/calendar/types';
import { AppCalendar } from '@/components/calendar/app-calendar';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useLanguage } from '@/contexts/language-context';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import {
  businessVisitsQueryKey,
  listBusinessVisits,
  type BusinessVisitDto,
} from '@/lib/api/jobs';

import { visitTone } from '../jobs/helpers';
import { VisitListItem } from './visit-list-item';

const VIEW_PARAM = parseAsStringLiteral(['list', 'calendar'] as const).withDefault(
  'list',
);

// How far ahead the list view looks — employees see a rolling schedule, not
// a paginated history.
const LIST_WINDOW_DAYS = 60;

export function EmployeeSchedule() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const [view, setView] = useQueryState('view', VIEW_PARAM);
  const [calDate, setCalDate] = useState(() => startOfMonth(new Date()));
  const [calView, setCalView] = useState<CalendarView>('month');

  const range = useMemo(() => {
    if (view !== 'calendar') return agendaRange(new Date(), LIST_WINDOW_DAYS);
    return calView === 'agenda'
      ? agendaRange(calDate, 30)
      : gridRange(getCalendarMatrix(startOfMonth(calDate)));
  }, [view, calDate, calView]);

  const visitsQuery = useQuery({
    queryKey: businessVisitsQueryKey(businessId ?? '', {
      scheduled_date_from: range.start,
      scheduled_date_to: range.end,
    }),
    queryFn: () =>
      listBusinessVisits(businessId!, {
        scheduled_date_from: range.start,
        scheduled_date_to: range.end,
      }),
    enabled: Boolean(businessId),
    placeholderData: keepPreviousData,
  });

  const visits = useMemo(() => visitsQuery.data ?? [], [visitsQuery.data]);
  const sortedVisits = useMemo(
    () => [...visits].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)),
    [visits],
  );

  function openVisit(visit: BusinessVisitDto) {
    navigate(`/schedule/${visit.job_id}/${visit.id}`);
  }

  const events = useMemo<CalendarEvent[]>(
    () =>
      visits.map((visit) => ({
        id: visit.id,
        date: visit.scheduled_date,
        label: visit.job_title,
        tone: visitTone(visit.status),
        meta: visit,
      })),
    [visits],
  );

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <CalendarDaysIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">{t.schedule}</h1>
            <p className="text-xs text-muted-foreground">
              {visits.length} {t.scheduleVisitsCount}
            </p>
          </div>
        </div>
        <ViewToggle
          value={view}
          onChange={(next) => void setView(next)}
          aria-label={t.schedule}
          options={[
            {
              value: 'list',
              label: t.listView,
              icon: ListIcon,
              testId: 'schedule-view-list',
            },
            {
              value: 'calendar',
              label: t.calendarView,
              icon: CalendarIcon,
              testId: 'schedule-view-calendar',
            },
          ]}
        />
      </div>

      {visitsQuery.isPending ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
      ) : view === 'calendar' ? (
        <AppCalendar
          events={events}
          date={calDate}
          view={calView}
          onNavigate={setCalDate}
          onView={setCalView}
          onSelectEvent={(event) => openVisit(event.meta as BusinessVisitDto)}
          isLoading={visitsQuery.isFetching}
          emptyLabel={t.calendarNoVisitsMonth}
          data-testid="schedule-calendar"
        />
      ) : sortedVisits.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">
          {t.scheduleEmpty}
        </p>
      ) : (
        <div className="flex flex-col gap-2" data-testid="schedule-list">
          {sortedVisits.map((visit) => (
            <VisitListItem key={visit.id} visit={visit} onSelect={openVisit} />
          ))}
        </div>
      )}
    </div>
  );
}
